'use client';

/**
 * Reusable Billing Capture Component
 *
 * Used to capture billing information inline within workflows:
 * - Agent workflow completions (outreach, assessments, care plans)
 * - Form submissions
 * - Care plan completions
 * - Member enrollment
 *
 * Features:
 * - Auto-populates available billing activities for the program
 * - Supports multiple line items
 * - Can create draft claim or save for batch submission
 * - Validates required fields
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  billingDependenciesAPI,
  ValidationResult,
  ValidationViolation
} from '@/services/api/billingDependencies';
import {
  DollarSign,
  Plus,
  Trash2,
  AlertCircle,
  AlertTriangle,
  Save,
  Send,
  X,
  Info,
  Sparkles
} from 'lucide-react';
import {
  DenialScoreBadge,
  DocCompletenessBadge,
  AutoCoderModal,
} from '@/components/billing/ai';

export interface BillingLineItem {
  billing_activity_id?: number;
  activity_name?: string;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  modifier_1?: string;
  modifier_2?: string;
  service_date: string;
  units: number;
  charge_amount: number;
  service_description?: string;
}

export interface BillingCaptureData {
  member_id: number;
  program_id?: number;
  payer_id?: number;
  organization_id?: number;
  service_date_from: string;
  service_date_to: string;
  place_of_service: string;
  diagnosis_code_1?: string;
  line_items: BillingLineItem[];
  internal_notes?: string;
  workflow_type?: string; // 'outreach', 'assessment', 'care_plan', 'enrollment'
  workflow_id?: number;
}

interface BillingActivity {
  id: number;
  activity_name: string;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  modifier_1?: string;
  modifier_2?: string;
  place_of_service?: string;
  rate_per_unit: number;
  billing_unit: string;
  activity_type?: string;
}

interface BillingCaptureFormProps {
  memberId: number;
  programId?: number;
  payerId?: number;
  organizationId?: number;
  workflowType?: 'outreach' | 'assessment' | 'care_plan' | 'enrollment' | 'form_completion';
  workflowId?: number;
  defaultData?: Partial<BillingCaptureData>;
  onSave?: (data: BillingCaptureData, asDraft: boolean) => Promise<void>;
  onCancel?: () => void;
  showInline?: boolean; // If true, displays compact inline version
  autoPopulateFromWorkflow?: boolean; // Auto-populate based on workflow type
}

export function BillingCaptureForm({
  memberId,
  programId,
  payerId,
  organizationId,
  workflowType,
  workflowId,
  defaultData,
  onSave,
  onCancel,
  showInline = false,
  autoPopulateFromWorkflow = true
}: BillingCaptureFormProps) {
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingActivities, setBillingActivities] = useState<BillingActivity[]>([]);
  const [lineItems, setLineItems] = useState<BillingLineItem[]>([]);
  const [validationResults, setValidationResults] = useState<Map<number, ValidationResult>>(new Map());
  const [showWarningOverride, setShowWarningOverride] = useState(false);
  // Billing AI in-flow state
  const [aiDenialVerdict, setAiDenialVerdict] = useState<'allow' | 'warn' | 'block' | null>(null);
  const [aiDocVerdict, setAiDocVerdict] = useState<'pass' | 'warn' | 'block' | null>(null);
  const [autoCoderOpen, setAutoCoderOpen] = useState(false);
  const [aiAcknowledged, setAiAcknowledged] = useState(false);
  // Optional encounter id (when this form is invoked from a workflow/encounter
  // promote flow). Denial scorer needs it; if absent, the badge is hidden.
  const [encounterId, setEncounterId] = useState<number | null>(
    (defaultData as any)?.encounter_id ?? null
  );

  const [formData, setFormData] = useState<BillingCaptureData>({
    member_id: memberId,
    program_id: programId,
    payer_id: payerId,
    organization_id: organizationId,
    service_date_from: new Date().toISOString().split('T')[0],
    service_date_to: new Date().toISOString().split('T')[0],
    place_of_service: '02', // Default to telehealth for social care
    line_items: [],
    workflow_type: workflowType,
    workflow_id: workflowId,
    ...defaultData
  });

  // Fetch billing activities for the program
  useEffect(() => {
    if (programId) {
      fetchBillingActivities();
    }
  }, [programId]);

  // Auto-populate billing activity based on workflow type
  useEffect(() => {
    if (autoPopulateFromWorkflow && workflowType && billingActivities.length > 0) {
      autoPopulateActivity();
    }
  }, [workflowType, billingActivities, autoPopulateFromWorkflow]);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  const fetchBillingActivities = async () => {
    try {
      const url = programId
        ? `/api/billing-activities?program_id=${programId}&is_active=true`
        : '/api/billing-activities?is_active=true';

      const response = await fetch(getWeaveinApiUrl(url), {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setBillingActivities(data);
      }
    } catch (err) {
      console.error('Error fetching billing activities:', err);
      setError('Failed to load billing activities');
    }
  };

  const autoPopulateActivity = () => {
    // Map workflow types to activity types
    const workflowToActivityType: Record<string, string> = {
      'outreach': 'care_coordination',
      'assessment': 'assessment',
      'care_plan': 'care_planning',
      'enrollment': 'case_management',
      'form_completion': 'care_coordination'
    };

    const activityType = workflowToActivityType[workflowType || ''];
    if (!activityType) return;

    // Find matching activity
    const matchingActivity = billingActivities.find(
      a => a.activity_type === activityType
    );

    if (matchingActivity && lineItems.length === 0) {
      // Auto-add the activity as first line item
      addLineItem(matchingActivity);
    }
  };

  // Validate billing activity for dependency rules (mutual exclusion, prerequisites, etc.)
  const validateActivity = useCallback(async (activityId: number, index: number) => {
    try {
      const result = await billingDependenciesAPI.validateBillingActivity({
        member_id: memberId,
        activity_id: activityId,
        service_date: formData.service_date_from,
        units: lineItems[index]?.units || 1
      });

      setValidationResults(prev => {
        const newMap = new Map(prev);
        newMap.set(index, result);
        return newMap;
      });

      // If there are blocking violations, show error
      const blockingViolations = result.violations.filter(
        v => v.severity === 'blocking' || v.severity === 'error'
      );
      if (blockingViolations.length > 0) {
        setError(blockingViolations.map(v => v.message).join('. '));
      } else {
        setError(null);
      }

      return result;
    } catch (err) {
      console.error('Error validating activity:', err);
      // Non-blocking - allow submission if validation service is unavailable
      return null;
    }
  }, [memberId, formData.service_date_from, lineItems]);

  // Check if there are any blocking violations
  const hasBlockingViolations = (): boolean => {
    for (const result of validationResults.values()) {
      if (result.violations.some(v => v.severity === 'blocking' || v.severity === 'error')) {
        return true;
      }
    }
    return false;
  };

  // Check if there are warnings that haven't been overridden
  const hasUnacknowledgedWarnings = (): boolean => {
    if (showWarningOverride) return false;
    for (const result of validationResults.values()) {
      if (result.warnings.length > 0) return true;
    }
    return false;
  };

  // Get all warnings from validation results
  const getAllWarnings = (): ValidationViolation[] => {
    const warnings: ValidationViolation[] = [];
    for (const result of validationResults.values()) {
      warnings.push(...result.warnings);
    }
    return warnings;
  };

  const addLineItem = (activity?: BillingActivity) => {
    const newItem: BillingLineItem = {
      billing_activity_id: activity?.id,
      activity_name: activity?.activity_name,
      cpt_code: activity?.cpt_code,
      hcpcs_code: activity?.hcpcs_code,
      revenue_code: activity?.revenue_code,
      modifier_1: activity?.modifier_1,
      modifier_2: activity?.modifier_2,
      service_date: formData.service_date_from,
      units: 1,
      charge_amount: activity?.rate_per_unit || 0,
      service_description: ''
    };

    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: keyof BillingLineItem, value: any) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const handleActivitySelect = async (index: number, activityId: number) => {
    const activity = billingActivities.find(a => a.id === activityId);
    if (activity) {
      // Update all line item fields with activity defaults
      const updated = [...lineItems];
      updated[index] = {
        ...updated[index],
        billing_activity_id: activity.id,
        activity_name: activity.activity_name,
        cpt_code: activity.cpt_code,
        hcpcs_code: activity.hcpcs_code,
        revenue_code: activity.revenue_code,
        modifier_1: activity.modifier_1,
        modifier_2: activity.modifier_2,
        charge_amount: activity.rate_per_unit
      };
      setLineItems(updated);

      // Validate the selected activity for dependency rules
      await validateActivity(activityId, index);
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.units * item.charge_amount), 0);
  };

  const handleSave = async (asDraft: boolean = true) => {
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return;
    }

    // Check for blocking violations
    if (hasBlockingViolations()) {
      setError('Cannot submit: Please resolve blocking violations before proceeding.');
      return;
    }

    // Check for warnings that haven't been acknowledged
    if (hasUnacknowledgedWarnings() && !asDraft) {
      setError('Please review warnings before submitting. Click "Acknowledge & Proceed" to continue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const billingData: BillingCaptureData = {
        ...formData,
        line_items: lineItems
      };

      if (onSave) {
        await onSave(billingData, asDraft);
      } else {
        // Default: create claim via API
        await createClaim(billingData, asDraft);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save billing data');
    } finally {
      setLoading(false);
    }
  };

  const createClaim = async (data: BillingCaptureData, asDraft: boolean) => {
    const response = await fetch(getWeaveinApiUrl('/api/claims'), {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        organization_id: data.organization_id,
        program_id: data.program_id,
        member_id: data.member_id,
        payer_id: data.payer_id,
        service_date_from: data.service_date_from,
        service_date_to: data.service_date_to,
        place_of_service: data.place_of_service,
        diagnosis_code_1: data.diagnosis_code_1,
        internal_notes: data.internal_notes,
        line_items: data.line_items.map((item, idx) => ({
          line_number: idx + 1,
          billing_activity_id: item.billing_activity_id,
          cpt_code: item.cpt_code,
          hcpcs_code: item.hcpcs_code,
          revenue_code: item.revenue_code,
          modifier_1: item.modifier_1,
          modifier_2: item.modifier_2,
          service_date: item.service_date,
          units: item.units,
          charge_amount: item.charge_amount,
          service_description: item.service_description
        }))
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create claim');
    }

    return response.json();
  };

  if (showInline) {
    // Compact inline version for workflow screens
    return (
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-blue-900">{t('billing.capture_billing', 'Capture Billing')}</h3>
            {lineItems.length > 0 && (
              <Badge className="bg-blue-100 text-blue-800">
                {lineItems.length} item{lineItems.length > 1 ? 's' : ''} - ${calculateTotal().toFixed(2)}
              </Badge>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 dark:border-red-700 rounded text-sm text-red-800 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <div className="space-y-3">
          {lineItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border">
              <select
                className="flex-1 p-1 border rounded text-sm"
                value={item.billing_activity_id || ''}
                onChange={(e) => handleActivitySelect(index, parseInt(e.target.value))}
              >
                <option value="">{t('billing.select_activity', 'Select activity...')}</option>
                {billingActivities.map(activity => (
                  <option key={activity.id} value={activity.id}>
                    {activity.activity_name} - {activity.cpt_code || activity.hcpcs_code} (${activity.rate_per_unit})
                  </option>
                ))}
              </select>
              <Input
                type="number"
                min="1"
                step="0.25"
                value={item.units}
                onChange={(e) => updateLineItem(index, 'units', parseFloat(e.target.value) || 1)}
                className="w-20 text-sm"
                placeholder={t('billing.units_1', 'Units')}
              />
              <span className="text-sm font-semibold text-gray-700 w-24 text-right">
                ${(item.units * item.charge_amount).toFixed(2)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeLineItem(index)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => addLineItem()}
              className="flex-1"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('billing.add_line_item', 'Add Line Item')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => handleSave(true)}
              disabled={loading || lineItems.length === 0}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-1" />
              {loading ? 'Saving...' : 'Save as Draft'}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Full form version for dedicated billing screens
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <DollarSign className="h-6 w-6" />
          {t('billing.billing_information', 'Billing Information')}
        </h2>
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:border-red-700 rounded flex items-center gap-2 text-red-800">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Service Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="service_date_from">{t('billing.service_date_from', 'Service Date From *')}</Label>
            <Input
              id="service_date_from"
              type="date"
              value={formData.service_date_from}
              onChange={(e) => setFormData({ ...formData, service_date_from: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="service_date_to">{t('billing.service_date_to', 'Service Date To *')}</Label>
            <Input
              id="service_date_to"
              type="date"
              value={formData.service_date_to}
              onChange={(e) => setFormData({ ...formData, service_date_to: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="place_of_service">{t('billing.place_of_service', 'Place of Service')}</Label>
            <select
              id="place_of_service"
              className="w-full p-2 border rounded"
              value={formData.place_of_service}
              onChange={(e) => setFormData({ ...formData, place_of_service: e.target.value })}
            >
              <option value="02">02 - Telehealth (Other than Patient's Home)</option>
              <option value="03">03 - School</option>
              <option value="04">04 - Homeless Shelter</option>
              <option value="09">09 - Prison/Correctional Facility</option>
              <option value="10">10 - Telehealth (Patient's Home)</option>
              <option value="11">11 - Office</option>
              <option value="12">12 - Home</option>
              <option value="13">13 - Assisted Living Facility</option>
              <option value="14">14 - Group Home</option>
              <option value="15">15 - Mobile Unit</option>
              <option value="16">16 - Temporary Lodging</option>
              <option value="17">17 - Walk-in Retail Health Clinic</option>
              <option value="18">18 - Place of Employment/Worksite</option>
              <option value="19">19 - Off Campus-Outpatient Hospital</option>
              <option value="20">20 - Urgent Care Facility</option>
              <option value="21">21 - Inpatient Hospital</option>
              <option value="22">22 - On Campus-Outpatient Hospital</option>
              <option value="23">23 - Emergency Room - Hospital</option>
              <option value="24">24 - Ambulatory Surgical Center</option>
              <option value="25">25 - Birthing Center</option>
              <option value="26">26 - Military Treatment Facility</option>
              <option value="31">31 - Skilled Nursing Facility</option>
              <option value="32">32 - Nursing Facility</option>
              <option value="33">33 - Custodial Care Facility</option>
              <option value="34">34 - Hospice</option>
              <option value="49">49 - Independent Clinic</option>
              <option value="50">50 - Federally Qualified Health Center</option>
              <option value="51">51 - Inpatient Psychiatric Facility</option>
              <option value="52">52 - Psychiatric Facility-Partial Hospitalization</option>
              <option value="53">53 - Community Mental Health Center</option>
              <option value="54">54 - Intermediate Care Facility</option>
              <option value="55">55 - Residential Substance Abuse Treatment</option>
              <option value="56">56 - Psychiatric Residential Treatment Center</option>
              <option value="57">57 - Non-residential Substance Abuse Treatment</option>
              <option value="60">60 - Mass Immunization Center</option>
              <option value="62">62 - Comprehensive Outpatient Rehab</option>
              <option value="65">65 - End-Stage Renal Disease Treatment</option>
              <option value="71">71 - Public Health Clinic</option>
              <option value="72">72 - Rural Health Clinic</option>
              <option value="81">81 - Independent Laboratory</option>
              <option value="99">99 - Other Place of Service</option>
            </select>
          </div>
          <div>
            <Label htmlFor="diagnosis_code">Diagnosis Code (Optional)</Label>
            <Input
              id="diagnosis_code"
              value={formData.diagnosis_code_1 || ''}
              onChange={(e) => setFormData({ ...formData, diagnosis_code_1: e.target.value })}
              placeholder="e.g., Z59.0"
            />
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-lg font-semibold">{t('billing.line_items', 'Line Items')}</Label>
            <Button variant="outline" size="sm" onClick={() => addLineItem()}>
              <Plus className="h-4 w-4 mr-1" />
              {t('billing.add_line_item_1', 'Add Line Item')}
            </Button>
          </div>

          {lineItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded">
              {t('billing.no_line_items_added_click', 'No line items added. Click "Add Line Item" to begin.')}
            </div>
          ) : (
            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>{t('billing.billing_activity', 'Billing Activity *')}</Label>
                      <select
                        className="w-full p-2 border rounded"
                        value={item.billing_activity_id || ''}
                        onChange={(e) => handleActivitySelect(index, parseInt(e.target.value))}
                      >
                        <option value="">{t('billing.select_activity_1', 'Select activity...')}</option>
                        {billingActivities.map(activity => (
                          <option key={activity.id} value={activity.id}>
                            {activity.activity_name} - {activity.cpt_code || activity.hcpcs_code} (${activity.rate_per_unit})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>{t('billing.service_date', 'Service Date')}</Label>
                      <Input
                        type="date"
                        value={item.service_date}
                        onChange={(e) => updateLineItem(index, 'service_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>{t('billing.units', 'Units')}</Label>
                      <Input
                        type="number"
                        min="1"
                        step="0.25"
                        value={item.units}
                        onChange={(e) => updateLineItem(index, 'units', parseFloat(e.target.value) || 1)}
                      />
                    </div>
                    <div>
                      <Label>{t('billing.charge_amount', 'Charge Amount')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.charge_amount}
                        onChange={(e) => updateLineItem(index, 'charge_amount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>{t('billing.line_total', 'Line Total')}</Label>
                      <div className="p-2 bg-white dark:bg-gray-800 border rounded font-semibold">
                        ${(item.units * item.charge_amount).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <Label>{t('billing.revenue_code', 'Revenue Code')}</Label>
                      <Input
                        value={item.revenue_code || ''}
                        onChange={(e) => updateLineItem(index, 'revenue_code', e.target.value)}
                        placeholder="e.g., 0510"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>{t('billing.modifier_1', 'Modifier 1')}</Label>
                      <Input
                        value={item.modifier_1 || ''}
                        onChange={(e) => updateLineItem(index, 'modifier_1', e.target.value.toUpperCase())}
                        placeholder="e.g., GT, HD"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <Label>{t('billing.modifier_2', 'Modifier 2')}</Label>
                      <Input
                        value={item.modifier_2 || ''}
                        onChange={(e) => updateLineItem(index, 'modifier_2', e.target.value.toUpperCase())}
                        placeholder="e.g., HE"
                        maxLength={2}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Description (Optional)</Label>
                      <Input
                        value={item.service_description || ''}
                        onChange={(e) => updateLineItem(index, 'service_description', e.target.value)}
                        placeholder={t('billing.additional_notes_about_this_service', 'Additional notes about this service')}
                      />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t('billing.remove_line_item', 'Remove Line Item')}
                      </Button>
                    </div>

                    {/* Validation Messages for this Line Item */}
                    {validationResults.get(index) && (
                      <div className="md:col-span-2 space-y-2">
                        {/* Blocking Violations */}
                        {validationResults.get(index)!.violations.map((violation, vIdx) => (
                          <div
                            key={`violation-${vIdx}`}
                            className={`p-2 rounded flex items-start gap-2 text-sm ${
                              violation.severity === 'blocking' || violation.severity === 'error'
                                ? 'bg-red-50 border border-red-200 text-red-800'
                                : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                            }`}
                          >
                            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium capitalize">{violation.type.replace(/_/g, ' ')}:</span>{' '}
                              {violation.message}
                            </div>
                          </div>
                        ))}
                        {/* Warnings */}
                        {validationResults.get(index)!.warnings.map((warning, wIdx) => (
                          <div
                            key={`warning-${wIdx}`}
                            className="p-2 bg-amber-50 border border-amber-200 rounded flex items-start gap-2 text-sm text-amber-800"
                          >
                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium capitalize">{warning.type.replace(/_/g, ' ')}:</span>{' '}
                              {warning.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              ))}

              {/* Warnings Acknowledgment Section */}
              {getAllWarnings().length > 0 && !showWarningOverride && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-amber-900">Warnings Require Acknowledgment</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        There are {getAllWarnings().length} warning(s) for this billing submission.
                        Review the warnings above and acknowledge to proceed.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => setShowWarningOverride(true)}
                      >
                        Acknowledge Warnings & Proceed
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {showWarningOverride && getAllWarnings().length > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded flex items-center gap-2 text-sm text-green-800">
                  <Info className="h-4 w-4" />
                  Warnings acknowledged. You may proceed with submission.
                </div>
              )}

              <div className="flex justify-end p-4 bg-gray-100 rounded">
                <div className="text-right">
                  <div className="text-sm text-gray-600 dark:text-gray-300">{t('billing.total_charge', 'Total Charge')}</div>
                  <div className="text-2xl font-bold text-green-600">
                    ${calculateTotal().toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Internal Notes */}
        <div>
          <Label htmlFor="internal_notes">Internal Notes (Optional)</Label>
          <Textarea
            id="internal_notes"
            value={formData.internal_notes || ''}
            onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
            placeholder="Internal notes (not sent to payer)"
            rows={3}
          />
        </div>

        {/* Billing AI in-flow checks */}
        {(encounterId || (formData.internal_notes && formData.internal_notes.trim().length > 60) || programId) && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-v2border-muted bg-paper-2 px-3 py-2 mt-2">
            <span className="text-xs font-medium text-ink-500 inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-v2accent" />
              AI checks
            </span>
            {encounterId && (
              <DenialScoreBadge
                encounterId={encounterId}
                refreshKey={lineItems.length + (formData.service_date_from?.length || 0)}
                onScoreChange={(s) => setAiDenialVerdict(s?.verdict || null)}
              />
            )}
            {formData.internal_notes && (
              <DocCompletenessBadge
                note={formData.internal_notes}
                programType={(workflowType === 'care_plan' ? 'ECM' : 'ECM') as any}
                encounterId={encounterId || undefined}
                onResultChange={(r) => setAiDocVerdict(r?.verdict || null)}
              />
            )}
            {programId && formData.internal_notes && formData.internal_notes.trim().length > 40 && (
              <button
                type="button"
                onClick={() => setAutoCoderOpen(true)}
                className="inline-flex items-center gap-1 rounded-full border border-v2border bg-paper px-2 py-1 text-[11px] font-medium text-ink hover:bg-paper-2"
              >
                <Sparkles className="h-3 w-3 text-v2accent" />
                Auto-code with Claude
              </button>
            )}
            {(aiDenialVerdict === 'block' || aiDocVerdict === 'block') && !aiAcknowledged && (
              <label className="ml-auto flex items-center gap-1.5 text-[11px] text-red-700">
                <input
                  type="checkbox"
                  checked={aiAcknowledged}
                  onChange={(e) => setAiAcknowledged(e.target.checked)}
                  className="h-3 w-3 accent-red-600"
                />
                I've reviewed the AI warnings
              </label>
            )}
          </div>
        )}

        {/* Auto-coder modal */}
        {programId && (
          <AutoCoderModal
            open={autoCoderOpen}
            onClose={() => setAutoCoderOpen(false)}
            note={formData.internal_notes || ''}
            programId={programId}
            onApply={(picks) => {
              // Append accepted suggestions to lineItems
              const additions: BillingLineItem[] = picks.map((p) => ({
                billing_activity_id: p.billing_activity_id,
                cpt_code: p.cpt_or_hcpcs?.startsWith('G') || p.cpt_or_hcpcs?.startsWith('T') || p.cpt_or_hcpcs?.startsWith('H') || p.cpt_or_hcpcs?.startsWith('S') || p.cpt_or_hcpcs?.startsWith('Z') ? undefined : p.cpt_or_hcpcs,
                hcpcs_code: p.cpt_or_hcpcs?.startsWith('G') || p.cpt_or_hcpcs?.startsWith('T') || p.cpt_or_hcpcs?.startsWith('H') || p.cpt_or_hcpcs?.startsWith('S') || p.cpt_or_hcpcs?.startsWith('Z') ? p.cpt_or_hcpcs : undefined,
                modifier_1: p.modifiers?.[0],
                modifier_2: p.modifiers?.[1],
                service_date: formData.service_date_from,
                units: p.units || 1,
                charge_amount: 0,
                service_description: p.rationale,
              }));
              setLineItems((prev) => [...prev, ...additions]);
            }}
          />
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              {t('billing.cancel', 'Cancel')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSave(true)}
            disabled={loading || lineItems.length === 0}
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button
            variant="default"
            onClick={() => handleSave(false)}
            disabled={
              loading ||
              lineItems.length === 0 ||
              hasBlockingViolations() ||
              ((aiDenialVerdict === 'block' || aiDocVerdict === 'block') && !aiAcknowledged)
            }
            title={
              hasBlockingViolations()
                ? 'Resolve blocking violations before submitting'
                : (aiDenialVerdict === 'block' || aiDocVerdict === 'block') && !aiAcknowledged
                ? 'Acknowledge the AI block warning before submitting'
                : ''
            }
          >
            <Send className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Create Claim'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default BillingCaptureForm;
